import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ErrorComponent from '@/components/error/error-component'
import { FilePdf, CheckCircle, Clock, AlertErrorCircle } from '@/assets/icons'
import { useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import { selectDataErrorForAsset, selectIsUpdateActive, selectTimelineAssetFiles, selectTimelineAssetForMatter } from '../../store/selectors'
import { TimelineAssetFileStatus, TimelineAssetFileView } from '../../store/schemas'
import { TimelineAssetActions } from '../../store/slice'
import RemoveFileDialog from './RemoveFileDialog'
import { SourceFileOverflowMenu } from './SourceFileOverflowMenu'

// Last Processed renders as two columns: a (relative) date + a 12-hour time.
function formatDate(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) return 'Today'
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}
function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

/**
 * The per-file status sub-line under the filename: a green check-circle + "N events extracted" for
 * the extracted/included happy state; spinner / clock / red alert for processing/pending/failed.
 */
function FileStatusLine({ file }: { file: TimelineAssetFileView }) {
  const events = `${file.events_extracted} ${file.events_extracted === 1 ? 'event' : 'events'} extracted`
  switch (file.status) {
    case TimelineAssetFileStatus.INCLUDED:
    case TimelineAssetFileStatus.EXTRACTED:
      return (
        <div className="flex items-center gap-1 text-green-800">
          <CheckCircle className="w-3.5 h-3.5 shrink-0" />
          <p className="text-xs font-medium">{events}</p>
        </div>
      )
    case TimelineAssetFileStatus.PROCESSING:
      return (
        <div className="flex items-center gap-1 text-brand-neutral-600">
          <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center">
            <CircularProgressContinuousSized size={12} thickness={4} />
          </span>
          <p className="text-xs font-medium italic">Processing…</p>
        </div>
      )
    case TimelineAssetFileStatus.PENDING:
      return (
        <div className="flex items-center gap-1 text-brand-neutral-600">
          <Clock className="w-3.5 h-3.5 shrink-0" />
          <p className="text-xs font-medium">Pending</p>
        </div>
      )
    case TimelineAssetFileStatus.FAILED:
      return (
        <div className="flex items-center gap-1 text-red-800">
          <AlertErrorCircle className="w-3.5 h-3.5 shrink-0" />
          <p className="text-xs font-medium">{file.error_reason || 'Processing failed'}</p>
        </div>
      )
    default:
      file.status satisfies never
      return null
  }
}

/**
 * Source Files tab: the timeline's file roster as a list of upload-card rows — PDF-icon gutter,
 * filename, an extraction status sub-line, a two-column Last Processed (date + time), and a kebab
 * overflow menu whose single action removes the file.
 */
export default function SourceFilesTab() {
  const dispatch = useAppDispatch()
  const assetId = useMatterAssetStoreKey()

  const { matterId } = useParams<{ matterId: string }>()
  const asset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId }))
  const files = useAppSelector((state: RootState) => selectTimelineAssetFiles(state, { assetId }))
  const dataError = useAppSelector((state: RootState) => selectDataErrorForAsset(state, { assetId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))

  const [fileToRemove, setFileToRemove] = useState<TimelineAssetFileView | null>(null)

  if (files === null) {
    if (dataError) {
      return (
        <ErrorComponent
          code="500"
          title="Error Loading Source Files"
          message={
            <>
              {'Our servers ran into a problem. Please '}
              <button
                onClick={() => assetId && dispatch(TimelineAssetActions.refreshAssetData({ assetId }))}
                className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
              >
                try again
              </button>
              {'.'}
            </>
          }
        />
      )
    }
    return (
      <div className="flex-1 flex items-center justify-center">
        <CircularProgressContinuousSized size={20} thickness={7} />
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto p-2 lg:p-4">
      {/* Header labels: summary (left) + Last Processed (right). */}
      <div className="flex items-center justify-between px-1 pb-3 text-sm font-medium tracking-[-0.15px] text-brand-neutral-800">
        {asset && (
          <span>
            {asset.total_events} {asset.total_events === 1 ? 'event' : 'events'} extracted from {files.length} {files.length === 1 ? 'document' : 'documents'}
          </span>
        )}
        {files.length > 0 && <span className="hidden sm:block sm:pr-[52px]">Last Processed</span>}
      </div>
      {/* Divider under the stats count. */}
      <hr className="mb-3 h-px border-0 bg-brand-neutral-200" />

      {files.length === 0 ? (
        <div className="rounded-[5px] border border-brand-neutral-200 bg-white px-3 py-6 text-sm text-brand-neutral-600 text-center">No source files yet.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {files.map((file) => (
            <div key={file.user_file_id} className="h-[60px] flex items-center gap-[10px] pr-3 rounded-[5px] border border-brand-neutral-200 bg-white">
              <div className="w-[60px] h-full flex items-center justify-center border-r border-brand-neutral-200 rounded-l-[5px]">
                <FilePdf className="w-6 h-6 text-brand-neutral-800" />
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-1 items-start justify-center">
                <p className="text-[13px] font-medium text-brand-neutral-950 truncate" title={file.display_name}>
                  {file.display_name}
                </p>
                <FileStatusLine file={file} />
              </div>
              <div className="hidden sm:flex items-center gap-1.5 text-[13px] font-normal tracking-[-0.15px] text-brand-neutral-800 shrink-0">
                <span className="w-20">{file.last_processed_at ? formatDate(file.last_processed_at) : '—'}</span>
                <span className="w-16">{file.last_processed_at ? formatTime(file.last_processed_at) : ''}</span>
              </div>
              <div className="shrink-0">
                <SourceFileOverflowMenu onRemove={() => setFileToRemove(file)} disabled={isUpdateActive} />
              </div>
            </div>
          ))}
        </div>
      )}

      {fileToRemove && assetId && matterId && (
        <RemoveFileDialog
          open={Boolean(fileToRemove)}
          assetId={assetId}
          matterId={matterId}
          userFileId={fileToRemove.user_file_id}
          displayName={fileToRemove.display_name}
          onClose={() => setFileToRemove(null)}
        />
      )}
    </div>
  )
}
